'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useMeasure, useMedia } from 'react-use';

import { AdaptiveLogo } from '@/components/adaptive-logo';
import VideoPlayer from '@/components/videoplayer';
import { clampvw } from '@/helpers/utils';

import { CaseStudyWrapper } from '../components/CaseStudyWrapper';
import { ImageData, ImageSlideshow } from '../components/ImageSlideshow';


export const ClientPage = () => {
  const val = clampvw(24, 44);
  const [gridRef, gridMeasure] = useMeasure<HTMLDivElement>();
  const isTablet = useMedia('(min-width: 768px)', false);

  const carouselImages: ImageData[] = [
    {
      src: '/images/carrot/slideshow_01.png',
      width: 2364,
      height: 1446,
      alt: 'Carrot Fertility team collaborating in a meeting room, discussing project plans.',
    },
    {
      src: '/images/carrot/slideshow_02.png',
      width: 2364,
      height: 1446,
      alt: 'Close-up of Carrot Fertility brand materials and design assets on a table.',
    },
    {
      src: '/images/carrot/slideshow_03.png',
      width: 2364,
      height: 1446,
      alt: 'Carrot Fertility members engaging in a virtual meeting, highlighting remote teamwork.',
    },
    {
      src: '/images/carrot/slideshow_01.png',
      width: 2364,
      height: 1446,
      alt: 'Carrot Fertility team collaborating in a meeting room, discussing project plans.',
    },
    {
      src: '/images/carrot/slideshow_02.png',
      width: 2364,
      height: 1446,
      alt: 'Close-up of Carrot Fertility brand materials and design assets on a table.',
    },
    {
      src: '/images/carrot/slideshow_03.png',
      width: 2364,
      height: 1446,
      alt: 'Carrot Fertility members engaging in a virtual meeting, highlighting remote teamwork.',
    },
  ];
  return (
    <CaseStudyWrapper
      mobileBackgroundImage="/images/carrot/carrot_hero_mobile_1x.png"
      backgroundImage="/images/carrot/carrot_hero.png"
      heading="Carrot Fertility"
      headingColor="text-white"
      backgroundColor="bg-carrot-dark"
      nextLink="/sora"
      hoverTextColor="text-carrot"
    >
      <Link href="/">
        <AdaptiveLogo blendMode={false} aria-label="Go to All Turtles Home" />
      </Link>
      <div className="relative z-20">
        <div className="bg-[#F1EFEB] pb-[84px] pt-[84px] tablet:pb-[164px] tablet:pt-[65px]">
          <div className="static-grid">
            <div className="col-span-full">
              <p className="mb-[45px] hidden justify-center font-breit text-[clamp(16px,calc(16px+0.875vw),30px)] font-normal uppercase leading-none tracking-wider text-[#F35B09] tablet:block">
                Carrot Fertility
              </p>
            </div>

            <div className="col-span-full grid grid-cols-12 mobile-only:space-y-[32px]">
              <div className="col-span-full tablet:col-span-8">
                <p className="font-soehne text-[clamp(18px,calc(18px+1.625vw),44px)] font-normal leading-[140%] tracking-[0.2098] text-navy">
                  Carrot started as a response to a personal difficulty with global implications. When CEO Tammy Sun
                  sought egg-freezing services for future family planning, she found herself in a complex, expensive
                  process not covered by her employer&apos;s health plan—a situation that places necessary care out of
                  reach for most people.
                </p>
              </div>

              <div className="col-span-full tablet:col-span-3 tablet:col-start-10">
                <p className="font-soehne text-[clamp(16px,calc(16px+0.125vw),18px)] font-buch leading-[200%] tracking-[0.21] text-[#F35B09]">
                  We provided
                </p>
                <ul className="list-none font-soehne text-[clamp(16px,calc(16px+0.125vw),18px)] font-normal uppercase leading-[150%] tracking-[0.21] text-navy tablet:col-span-2 tablet:col-start-11 desktop:mt-0 desktop:leading-[200%]">
                  <li>Brand + Design</li>
                  <li>Product + Engineering</li>
                  <li>Intellectual Property</li>
                  <li>Finance </li>
                  <li>Recriting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white">
          <div className="static-grid overflow-clip pt-16 tablet:pt-[230px]">
            <div className="col-span-full flex flex-col space-y-[32px] tablet:col-span-6 tablet:space-y-[45px]">
              <h2 className="liga font-heldane text-[clamp(38px,calc(38px+3.25vw),90px)] font-buch leading-[110%] tracking-[-2px] text-[#F35B09] ligatures-common">
                Fertility and family-building care for everyone
              </h2>
              <p className="pb-[80px] font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21] text-navy">
                Founded in 2016, Carrot&apos;s mission is to ensure that every person, regardless of their circumstances
                or journey, has affordable access to fertility care and family-building support. It&apos;s precisely the
                sort of grounded, impactful challenge that appeals to us at All Turtles, and our expertise in product
                and brand development has been a perfect fit for Carrot&apos;s evolving needs.
              </p>
            </div>
            <div className="col-span-full flex items-end justify-end tablet:col-span-5 tablet:col-start-8">
              <Image
                className="relative"
                src="/images/carrot/carrot_harper.png"
                alt="Portrait of Harper, Carrot Fertility team member"
                width={621}
                height={782}
                priority
              />
            </div>
          </div>
        </div>
        <div className="bg-[#343434]">
          {isTablet ? (
            <Image
              className="h-auto w-full"
              src="/images/carrot/animation.png"
              alt="Carrot Fertility brand animation showing growth and connection"
              width={1920}
              height={918}
              priority
            />
          ) : (
            <Image
              className="h-auto w-full"
              src="/images/carrot/animation-mobile.png"
              alt="Carrot Fertility mobile brand animation illustrating inclusivity"
              width={1323}
              height={1068}
              priority
            />
          )}
        </div>

        <div className="bg-white">
          <div className="static-grid text-left text-[#F35B09]">
            <div className="col-span-full py-12 tablet:col-span-10 tablet:col-start-2 tablet:pb-[190px] tablet:pt-[127px]">
              <p className="text-[#F35B09]-dark font-soehne text-[clamp(18px,calc(18px+1.5vw),42px)] leading-[172%] tracking-[0.21px]">
                Together, All Turtles and Carrot created a brand that solidifies their position as the leading global
                fertility and family-building platform. Today, millions of members around the world trust Carrot for
                personalized and inclusive care in 130+ countries with 10,000+ eligible providers.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white">
          <div className="static-grid text-navy" ref={gridRef}>
            <div className="relative col-span-full mobile-only:pb-[45px] tablet:col-span-7">
              <Image
                className={`!h-auto !w-full transition-opacity duration-300 tablet:absolute tablet:right-0 tablet:top-0 ${
                  gridMeasure.width && isTablet !== undefined ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  transform: isTablet ? `translateX(-${gridMeasure.width / 12}px)` : 'translateX(0)',
                }}
                src="/images/carrot/carrot_group.png"
                alt="Group photo of Carrot Fertility team members"
                width={960}
                height={752}
              />
            </div>
            <div className="col-span-full space-y-[32px] tablet:col-span-5 tablet:col-start-8 tablet:space-y-[45px]">
              <h2 className="liga pb-[15px] font-heldane text-[clamp(38px,calc(38px+3.25vw),90px)] font-buch leading-[110%] tracking-[-2%] text-[#F35B09]">
                A fertility care platform that stands above the rest
              </h2>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                In 2022, we helped Carrot revisit their brand in the face of a rapidly changing and increasingly crowded
                industry. Carrot wanted to double down on their visionary blend of science-driven outcomes and a caring,
                compassionate approach that makes these outcomes accessible to everyone.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                We leaned into Carrot&apos;s mission of supporting every journey, from egg and sperm freezing to IVF,
                gestational surrogacy, adoption, gender-affirming care, and menopause. Carrot members and their stories
                became a key focus of the brand, with website and marketing materials showcasing real Carrot members and
                highlighting the industry-leading and life-changing results they received.
              </p>
            </div>
          </div>

          <div className="py-12">
            <ImageSlideshow
              images={carouselImages}
              aspectRatio="aspect-[2/1]"
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
            />
          </div>

          <div className="static-grid">
            <div className="relative col-span-full mobile-only:order-1 mobile-only:pb-[45px] tablet:col-span-7 tablet:flex tablet:items-center tablet:justify-end">
              <div
                className={`!h-auto !w-full transition-opacity duration-300 ${
                  gridMeasure.width && isTablet !== undefined ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  transform: isTablet ? `translateX(-${gridMeasure.width / 12}px)` : 'translateX(0)',
                }}
              >
                <VideoPlayer
                  sources={[{ src: '/videos/carrot-page-flip.mp4' }]}
                  controls={false}
                  loop={true}
                  gif="/videos/carrot-page-flip-med.gif"
                />
              </div>
            </div>
            <div className="col-span-full space-y-[45px] mobile-only:order-2 tablet:col-span-5">
              <h2 className="liga pb-[15px] font-heldane text-[clamp(38px,calc(38px+3.25vw),90px)] font-buch leading-[110%] tracking-[-2%] text-[#F35B09] desktop:pt-[84px]">
                Supporting every person, on every journey
              </h2>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                With All Turtles&apos; help, Carrot re-established itself as best-in-class and clearly differentiated
                from their competitors. We created a bold visual language that speaks to Carrot&apos;s human and
                data-driven nature, using striking colors, a strong typeface, and compelling compositions.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                Carrot&apos;s brand messaging, positioning, and story showcase their scientific authority and clinically
                validated approach to fertility and family-building, but also represent the approachable values that set
                Carrot apart.
              </p>
            </div>

            <div className="col-span-full space-y-[32px] tablet:col-span-5 tablet:space-y-[45px]">
              <p className="liga font-heldane text-[clamp(24px,calc(24px+1.25vw),44px)] leading-[133%] tracking-[0.21px] text-[#F35B09] tablet:pt-[135px]">
                Instead of a one-size-fits-all approach, Carrot provides access to inclusive, personalized care based on
                an individual’s unique needs to achieve the best outcomes.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                That same blend of clinical rigor, data-driven insights, and personalized care drove another
                collaboration between All Turtles and Carrot beginning in 2025. A cross-disciplinary team of experts
                from both companies partnered to design and launch Sprints—a metabolic-fertility health program that
                helps members improve outcomes by addressing the underlying fundamentals of nutrition, movement, sleep,
                and stress management.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                To succeed, product strategy had to merge with UX, brand, and content design to translate a clinically
                complex, multi-factor program into an easy-to-use, motivating, and visually compelling app-based
                experience.
              </p>
            </div>
            <div className="col-span-full pt-12 tablet:col-span-6 tablet:col-start-7 tablet:pt-[135px]">
              <Image
                className="relative"
                src="/images/carrot/carrot_goals.png"
                alt="Carrot Fertility goals infographic showing key milestones and achievements"
                width={860}
                height={538}
              />
            </div>
            <div className="col-span-full py-12 tablet:py-[128px]">
              <p className="font-soehne text-[clamp(24px,calc(24px+1.25vw),44px)] leading-[172%] tracking-[0.21px] text-[#F35B09]">
                Together, we built an end-to-end experience that inspires trust by balancing scientific rigor with human
                warmth. Sprints explains the science simply and provides a personalized plan, ensuring members feel
                confident, understood, and ready to take action.
              </p>
            </div>
          </div>
          <div className="mobile-only:pt-[36px]">
            <VideoPlayer
              sources={[{ src: '/videos/carrot.mp4' }]}
              controls={false}
              loop={true}
              gif="/videos/carrot_10_sec.gif"
            />
          </div>
        </div>
      </div>
    </CaseStudyWrapper>
  );
};
